/**
 * LandingPageElite — QuickBooks/Xero-inspired light marketing page.
 * Design blueprint: /app/design_guidelines.json (Playfair Display + Manrope,
 * crystal-glass nav, bento AI grid, dark-green migration band, tiered pricing).
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Check, Sparkles, FileText, Landmark, BarChart3, ScanLine,
  MessageSquare, ShieldCheck, Clock, Zap, ChevronDown, Star, Receipt,
  ArrowRightLeft, Lock, Menu, X, Moon, Bot, Eye, GitBranch,
  BookOpen, CreditCard, PenTool, Calculator, Users,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const TIERS = [
  { id: 'starter', name: 'Starter', price: 19.99, tagline: 'For solo operators & home',
    features: ['Single user (owner only)', '200 AI credits/month', '2 GB storage', '1 bank account · Plaid sync', 'OCR + AI auto-categorize'] },
  { id: 'pro', name: 'Pro', price: 49.99, tagline: 'Small businesses & consultants',
    features: ['5 users', '1,500 AI credits/month', '25 GB storage', '4 banks · multi-workspace', 'Daily Brief · e-signatures · Stripe · Time tracking'] },
  { id: 'business', name: 'Business', price: 99.99, popular: true, tagline: 'Most popular',
    features: ['Unlimited users', '4,000 AI credits/month', '100 GB storage', '10 banks', 'Gmail · Calendar · API access'] },
];

const WHITE_GLOVE = [
  { id: 'done_for_you', name: 'Done For You', price: 299.99, tagline: 'A CPA or EA does it all',
    features: ['Everything in Business', 'Dedicated CPA / EA bookkeeper', 'All bookkeeping done monthly', '5-day month-end close', 'White-glove QuickBooks migration'] },
  { id: 'platinum', name: 'Platinum CFO', price: 599, tagline: 'Your own CountBucks CPA',
    features: ['Everything in Done For You', 'Tax return prep & filing included', '30-min monthly CPA call', '6,000 AI credits · 250 GB storage', 'White-glove QuickBooks migration'] },
];

const AI_LOGOS = [
  { name: 'OpenAI · ChatGPT', src: '/logos/ai/openai.svg' },
  { name: 'Anthropic · Claude', src: '/logos/ai/claude-color.svg' },
  { name: 'Google · Gemini', src: '/logos/ai/gemini-color.svg' },
  { name: 'xAI · Grok', src: '/logos/ai/grok.svg' },
  { name: 'Meta · Llama', src: '/logos/ai/meta-color.svg' },
  { name: 'Mistral', src: '/logos/ai/mistral-color.svg' },
  { name: 'DeepSeek', src: '/logos/ai/deepseek-color.svg' },
  { name: 'Perplexity', src: '/logos/ai/perplexity-color.svg' },
];

const REPLACES = [
  { name: 'QuickBooks Online', abbr: 'QB', price: '$85 /mo' },
  { name: 'Dext / Hubdoc', abbr: 'DX', price: '$25 /mo' },
  { name: 'Bill.com', abbr: 'BC', price: '$45 /mo' },
  { name: 'DocuSign', abbr: 'DS', price: '$35 /mo' },
  { name: 'Expensify', abbr: 'EX', price: '$20 /mo' },
  { name: 'Google Drive (business)', abbr: 'GD', price: '$12 /mo' },
  { name: 'ChatGPT Plus for finance', abbr: 'GPT', price: '$20 /mo' },
  { name: 'Part-time bookkeeper', abbr: 'BK', price: '$170 /mo' },
];

const AI_TEAM = [
  { slug: 'ai-buck', name: 'AI Buck', role: 'Bookkeeper', Icon: ScanLine, bg: 'bg-emerald-600',
    blurb: 'Reads every receipt & invoice, categorizes transactions, matches to bank feeds — 24/7.',
    activity: 'Categorized 47 transactions · 12 min ago' },
  { slug: 'cfo-buck', name: 'CFO Buck', role: 'Analyst', Icon: MessageSquare, bg: 'bg-[#064E3B]',
    blurb: 'Answers any question about your numbers in plain English. Builds forecasts on demand.',
    activity: 'Drafted Q1 cash-flow forecast · 2 min ago' },
  { slug: 'tax-buck', name: 'Tax Buck', role: 'Deduction hunter', Icon: Landmark, bg: 'bg-amber-600',
    blurb: 'Hunts deductions year-round, flags tax-saving moves, and preps your Schedule C.',
    activity: 'Found $2,340 in missed home-office deductions' },
  { slug: 'chaser-buck', name: 'Chaser Buck', role: 'Collections', Icon: Receipt, bg: 'bg-indigo-600',
    blurb: 'Politely chases overdue invoices, drafts follow-ups and books payment plans.',
    activity: 'Recovered $4,120 in receivables this week' },
];

const DAY_TIMELINE = [
  { time: '6:00 AM', title: 'Overnight transactions imported', who: 'AI Buck', bg: 'bg-emerald-600', Icon: Moon,
    desc: 'Plaid pulls fresh bank activity. AI Buck categorizes 100% of it before you finish your coffee.' },
  { time: '9:30 AM', title: 'Receipts arrive by email', who: 'AI Buck', bg: 'bg-emerald-500', Icon: ScanLine,
    desc: 'You forward the DoorDash receipt — matched to a transaction and filed automatically.' },
  { time: '12:15 PM', title: 'Invoice sent from your phone', who: 'Chaser Buck', bg: 'bg-indigo-600', Icon: Receipt,
    desc: 'Client pays with Apple Pay before dessert. A thank-you goes out and receivables update in real time.' },
  { time: '3:00 PM', title: 'Anomaly caught', who: 'AI Buck', bg: 'bg-amber-500', Icon: ShieldCheck,
    desc: 'A $2,800 duplicate vendor charge is flagged for your approval — before it hits your reconciliation.' },
  { time: '6:45 PM', title: 'Tax deduction discovered', who: 'Tax Buck', bg: 'bg-amber-600', Icon: Landmark,
    desc: 'Tax Buck spotted $340 of unclaimed mileage from your calendar this week. Applied with one tap.' },
  { time: '11:00 PM', title: 'Daily Brief in your inbox', who: 'CFO Buck', bg: 'bg-[#064E3B]', Icon: MessageSquare,
    desc: "Tomorrow's cash position, top vendor owed, and one action worth taking — sent while you sleep." },
];

const CHAT_PROMPTS = [
  { q: 'Am I profitable this quarter?',
    a: 'Yes — net profit is $18,440, up 12% vs last quarter. The biggest driver was faster invoice collection (average pay time dropped from 27 → 14 days).' },
  { q: 'What can I write off before year-end?',
    a: "I found 3 opportunities:\n• $2,340 unclaimed home-office (172 sq ft × $5 /sq ft)\n• $1,200 Section 179 on the new laptop\n• $890 in mileage still un-logged from your calendar\n\nTotal potential deduction: $4,430." },
  { q: 'Who owes me money right now?',
    a: 'You have $12,650 in receivables from 4 customers. Acme Co. is 22 days late on invoice #0142 ($4,800). Want me to send them a friendly nudge?' },
];

const SAFETY = [
  { Icon: Eye, t: 'Grounded in your data',
    d: "Every AI answer cites the transaction, invoice or document it came from. If it can't cite it, it won't say it." },
  { Icon: GitBranch, t: 'Human-approve mode',
    d: 'Toggle on for AI to draft everything but require your one-tap approval before anything posts to your books.' },
  { Icon: Clock, t: 'Full audit trail',
    d: 'Every AI action is logged with model, prompt and diff — reviewable and reversible for 90 days.' },
  { Icon: Lock, t: 'Your data stays yours',
    d: 'We never train models on your books. Encrypted at rest and in transit, with read-only bank access via Plaid.' },
];

const FIRM_MENU = [
  { Icon: BookOpen, title: 'Bookkeeping & GL', tint: 'emerald',
    items: ['Chart of accounts', 'Journal entries', 'General ledger', 'Bank reconciliation', 'Recurring entries', 'Statement importer', 'Batch import & CSV', 'Full audit log'] },
  { Icon: CreditCard, title: 'Invoicing & payments', tint: 'indigo',
    items: ['Branded invoices', 'Bills & bill pay', 'Recurring invoices', 'Stripe · ACH · Apple Pay', 'Public pay pages', 'Auto-reminders', 'Payment plans', 'Customer statements'] },
  { Icon: PenTool, title: 'Documents & e-signatures', tint: 'sky',
    items: ['Document Hub & folders', 'Email-forward inbox', 'AI OCR & extraction', 'E-signatures & envelopes', 'Folder automations', 'Templates library', 'Public folder shares', 'Client portal delivery'] },
  { Icon: Calculator, title: 'Tax & reports', tint: 'amber',
    items: ['Real-time P&L', 'Balance sheet & cash flow', 'Tax Mapper (Schedule C)', 'AI tax-savings finder', 'Public tax-savings reports', 'DSCR & lender packages', 'Custom report builder', 'Year-end close checklist'] },
  { Icon: Bot, title: 'AI agents & assistants', tint: 'rose',
    items: ['AI Buck bookkeeper', 'CFO Buck analyst', 'Reconcile Buck', 'AI Command Center', 'AI Documents Q&A', 'Memory Hub context', 'Daily Brief email', 'AI hallucination safeguards'] },
  { Icon: Users, title: 'Team & client ops', tint: 'violet',
    items: ['Contacts & CRM-lite', 'Time tracking & billable hours', 'Task manager & checklists', 'Team roles & permissions', 'Client portal & messages', 'Guided tours', 'Support console & tickets', 'University (training library)'] },
];

const TINTS = {
  emerald: { badge: 'bg-emerald-600', bg: 'bg-emerald-50', dot: 'bg-emerald-500' },
  indigo:  { badge: 'bg-indigo-600',  bg: 'bg-indigo-50',  dot: 'bg-indigo-500' },
  sky:     { badge: 'bg-sky-600',     bg: 'bg-sky-50',     dot: 'bg-sky-500' },
  amber:   { badge: 'bg-amber-600',   bg: 'bg-amber-50',   dot: 'bg-amber-500' },
  rose:    { badge: 'bg-rose-600',    bg: 'bg-rose-50',    dot: 'bg-rose-500' },
  violet:  { badge: 'bg-violet-600',  bg: 'bg-violet-50',  dot: 'bg-violet-500' },
};

const FAQS = [
  { q: 'What is CountBucks and how does it work?',
    a: 'CountBucks is cloud accounting software with a real AI team built in. Connect your bank, drop in your documents, and AI Buck reads, categorizes, and files everything — while CFO Buck answers any question about your numbers in plain English.' },
  { q: 'Can I really switch from QuickBooks in 3 minutes?',
    a: 'Yes. Our migration tool imports your chart of accounts, customers, vendors, and open invoices from a QuickBooks export. Fresh Start mode takes about 3 minutes; white-glove plans include a full historical migration done for you.' },
  { q: 'Which AI models power CountBucks?',
    a: 'CountBucks routes each job to the best model for the task — OpenAI, Anthropic Claude, Google Gemini, xAI Grok, Meta Llama, Mistral, DeepSeek and more — all included in your plan. No separate AI subscriptions needed.' },
  { q: 'Is my financial data secure?',
    a: 'Bank connections run through Plaid with read-only access, documents are stored encrypted, payments run on Stripe, and your books are protected by role-based access with a full audit log.' },
  { q: 'Do you offer bookkeeping done for me?',
    a: 'Yes — Done For You and Platinum CFO plans pair you with a dedicated CPA or EA who closes your books monthly. Platinum even includes your annual tax return preparation and filing.' },
];

const SIGNUP = '/login?mode=register&plan=starter&cadence=monthly';

const serif = { fontFamily: "'Playfair Display', Georgia, serif" };
const sans = { fontFamily: "'Manrope', system-ui, sans-serif" };

function CheckItem({ children }) {
  return (
    <li className="flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed">
      <span className="mt-0.5 w-4 h-4 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
        <Check className="w-2.5 h-2.5" strokeWidth={3} />
      </span>
      {children}
    </li>
  );
}

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.55, ease: [0.21, 0.47, 0.32, 0.98] },
};

export default function LandingPageElite() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [mobileNav, setMobileNav] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [activePrompt, setActivePrompt] = useState(0);

  useEffect(() => {
    let alive = true;
    checkAuth?.().then((u) => { if (alive && u) navigate('/dashboard', { replace: true }); }).catch(() => {});
    return () => { alive = false; };
  }, [checkAuth, navigate]);

  return (
    <div className="min-h-screen bg-white text-[#022C22] antialiased" style={sans} data-testid="landing-elite">
      {/* ── Sticky crystal-glass header ─────────────────────────────── */}
      <header className="fixed top-0 inset-x-0 z-50 bg-white/70 backdrop-blur-xl saturate-150 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <span className="w-8 h-8 rounded-lg bg-emerald-600 text-white flex items-center justify-center font-extrabold text-sm">CB</span>
            <span className="font-extrabold text-lg tracking-tight">CountBucks</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-semibold text-slate-600">
            <a href="#features" className="hover:text-emerald-700 transition-colors" data-testid="nav-features">Features</a>
            <a href="#ai" className="hover:text-emerald-700 transition-colors" data-testid="nav-ai">AI Models</a>
            <a href="#switch" className="hover:text-emerald-700 transition-colors" data-testid="nav-switch">Switch from QuickBooks</a>
            <a href="#pricing" className="hover:text-emerald-700 transition-colors" data-testid="nav-pricing">Pricing</a>
          </nav>
          <div className="hidden md:flex items-center gap-3">
            <Link to="/login" className="text-sm font-semibold text-slate-700 hover:text-emerald-700 transition-colors" data-testid="nav-login">Log in</Link>
            <Link to={SIGNUP} data-testid="nav-get-started"
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-5 py-2.5 transition-[background-color,transform] hover:-translate-y-0.5">
              Get started <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <button className="md:hidden p-2" onClick={() => setMobileNav(v => !v)} data-testid="nav-mobile-toggle" aria-label="Menu">
            {mobileNav ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {mobileNav && (
          <div className="md:hidden bg-white border-t border-slate-200 px-5 py-4 space-y-3 text-sm font-semibold text-slate-700" data-testid="nav-mobile-menu">
            <a href="#features" className="block" onClick={() => setMobileNav(false)}>Features</a>
            <a href="#ai" className="block" onClick={() => setMobileNav(false)}>AI Models</a>
            <a href="#switch" className="block" onClick={() => setMobileNav(false)}>Switch from QuickBooks</a>
            <a href="#pricing" className="block" onClick={() => setMobileNav(false)}>Pricing</a>
            <div className="pt-3 border-t border-slate-100 flex gap-3">
              <Link to="/login" className="flex-1 text-center py-2.5 rounded-full border border-slate-300">Log in</Link>
              <Link to={SIGNUP} className="flex-1 text-center py-2.5 rounded-full bg-emerald-600 text-white">Get started</Link>
            </div>
          </div>
        )}
      </header>

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="pt-32 pb-20 sm:pt-40 sm:pb-28 px-5 sm:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_80%_0%,#ECFDF5_0%,transparent_60%)]" />
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center relative">
          <motion.div className="lg:col-span-6" initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3.5 py-1.5 mb-6">
              <Sparkles className="w-3.5 h-3.5" /> AI-powered accounting
            </p>
            <h1 style={serif} className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[1.05] mb-6">
              Run your business.<br />We'll do the <em className="text-emerald-700">accounting.</em>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed max-w-xl mb-8">
              CountBucks pairs beautifully simple books with a real AI team — drop in any
              receipt or invoice and it's read, categorized, and filed before your coffee cools.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Link to={SIGNUP} data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-7 py-3.5 text-base transition-[background-color,transform] hover:-translate-y-0.5">
                Start 7 days free <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#pricing" data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 hover:border-emerald-600 hover:text-emerald-700 font-bold px-7 py-3.5 text-base transition-colors">
                See plans &amp; pricing
              </a>
            </div>
            <p className="mt-5 text-sm text-slate-500 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-600" /> 7 days free, then $19.99/mo · Cancel before day 8 and pay $0
            </p>
          </motion.div>

          {/* Floating testimonial + mini dashboard */}
          <motion.div className="lg:col-span-6 relative" initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}>
            <div className="rounded-2xl border border-slate-200 bg-white shadow-[0_24px_60px_-24px_rgba(2,44,34,0.25)] overflow-hidden">
              <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between bg-slate-50/60">
                <p className="text-sm font-bold">Cash flow · This month</p>
                <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 rounded-full px-2.5 py-1">+18.4%</span>
              </div>
              <div className="p-5 grid grid-cols-3 gap-4">
                {[['Money in', '$48,220'], ['Money out', '$29,780'], ['Net', '$18,440']].map(([k, v]) => (
                  <div key={k}>
                    <p className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">{k}</p>
                    <p className="text-lg font-extrabold">{v}</p>
                  </div>
                ))}
                <div className="col-span-3 h-24 flex items-end gap-1.5">
                  {[38, 52, 44, 66, 58, 74, 62, 84, 71, 92, 80, 96].map((h, i) => (
                    <div key={i} className="flex-1 rounded-t-md bg-gradient-to-t from-emerald-600 to-emerald-400" style={{ height: `${h}%` }} />
                  ))}
                </div>
              </div>
            </div>
            <div className="absolute -bottom-8 -left-4 sm:-left-10 max-w-xs rounded-2xl border border-slate-200 bg-white shadow-xl p-4 flex gap-3.5 items-start">
              <img src="https://images.pexels.com/photos/8422729/pexels-photo-8422729.jpeg?auto=compress&cs=tinysrgb&w=160" alt="Business owner"
                className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
              <div>
                <p className="text-[13px] text-slate-700 leading-snug">“AI Buck files every receipt for me. I got my Sundays back.”</p>
                <p className="mt-1.5 text-[11px] font-bold text-slate-500">Marcus T · Retail owner</p>
              </div>
            </div>
            <div className="absolute -top-5 -right-2 sm:-right-6 rounded-full bg-[#064E3B] text-white text-xs font-bold px-4 py-2.5 shadow-lg flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-emerald-300" /> Save 13+ hrs / month
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Outcome stats bar ───────────────────────────────────────── */}
      <section className="border-y border-slate-200 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 grid sm:grid-cols-3 gap-8">
          {[
            ['Every document filed by AI', 'Invoices, receipts & statements auto-detected and organized', ScanLine],
            ['3-minute QuickBooks switch', 'Chart of accounts, contacts & open invoices imported for you', ArrowRightLeft],
            ['Answers in plain English', 'Ask CFO Buck anything about your numbers, any time', MessageSquare],
          ].map(([t, s, Icon]) => (
            <div key={t} className="flex gap-4">
              <span className="w-11 h-11 rounded-xl bg-white border border-slate-200 text-emerald-700 flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5" />
              </span>
              <div>
                <p className="font-extrabold leading-tight">{t}</p>
                <p className="text-sm text-slate-500 leading-relaxed mt-1">{s}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── One login, no more tool stack ───────────────────────────── */}
      <section id="all-in-one" className="py-24 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp} className="max-w-2xl mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700 mb-3">One login · one bill · one platform</p>
            <h2 style={serif} className="text-3xl sm:text-4xl font-semibold tracking-tighter leading-tight">
              Cancel the <em className="text-emerald-700">tool graveyard.</em>
            </h2>
            <p className="text-slate-600 leading-relaxed mt-4 max-w-xl">
              Small businesses stitch together 6–8 apps to run their books. CountBucks folds them
              into one AI-native workspace — for a fraction of the cost.
            </p>
          </motion.div>

          <motion.div {...fadeUp} className="grid lg:grid-cols-12 gap-6" data-testid="one-stop-shop">
            <div className="lg:col-span-7 rounded-2xl border border-slate-200 bg-[#F8FAFC] p-8">
              <p className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500 mb-5">You currently pay for →</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {REPLACES.map((r) => (
                  <div key={r.name} className="flex items-center justify-between gap-3 rounded-lg bg-white border border-slate-200 px-4 py-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className="w-8 h-8 rounded-md bg-slate-100 flex items-center justify-center text-[10px] font-extrabold text-slate-500 flex-shrink-0">{r.abbr}</span>
                      <span className="text-sm font-bold text-slate-700 truncate">{r.name}</span>
                    </div>
                    <span className="text-xs font-bold text-slate-400 line-through flex-shrink-0">{r.price}</span>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-sm font-bold text-slate-500">Monthly damage: <span className="text-slate-900 text-lg">$412+ /mo</span></p>
            </div>

            <div className="lg:col-span-5 rounded-2xl bg-[#022C22] text-white p-8 flex flex-col justify-between relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-emerald-500/20 blur-2xl" />
              <div className="relative">
                <p className="text-[11px] font-extrabold uppercase tracking-wider text-emerald-300 mb-5">Switch to →</p>
                <div className="flex items-center gap-3 mb-5">
                  <span className="w-11 h-11 rounded-xl bg-emerald-500 text-white flex items-center justify-center font-extrabold">CB</span>
                  <span className="text-2xl font-extrabold">CountBucks</span>
                </div>
                <ul className="space-y-2 text-sm text-emerald-100/90">
                  {['AI bookkeeping & OCR', 'Invoicing & Stripe payments', 'Receipts by email forwarding', 'Bill pay & vendor tracking', 'E-signatures & document hub', 'Reports & tax-savings finder', 'Client portal & support console', 'Audit trail on every AI action'].map((x) => (
                    <li key={x} className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-300 flex-shrink-0" strokeWidth={3} />{x}</li>
                  ))}
                </ul>
              </div>
              <div className="relative mt-8 pt-6 border-t border-white/10">
                <p className="text-sm text-emerald-100/70">From</p>
                <p className="text-4xl font-extrabold tracking-tight">$49.99<span className="text-base font-semibold text-emerald-100/70"> /mo</span></p>
                <p className="mt-1 text-sm font-bold text-emerald-300">Save $363+ every month</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── AI model logo wall ──────────────────────────────────────── */}
      <section id="ai" className="py-24 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp} className="max-w-2xl mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700 mb-3">Built-in AI, no extra subscriptions</p>
            <h2 style={serif} className="text-3xl sm:text-4xl font-semibold tracking-tighter leading-tight">
              One plan. The world's <em className="text-emerald-700">best AI</em> working your books.
            </h2>
            <p className="text-slate-600 leading-relaxed mt-4">
              CountBucks routes every job — reading a receipt, forecasting cash flow, drafting an
              invoice chase — to the model that does it best. All included.
            </p>
          </motion.div>
          <motion.div {...fadeUp} className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="ai-logo-wall">
            {AI_LOGOS.map((l) => (
              <div key={l.name} data-testid={`ai-logo-${l.name.split(' ')[0].toLowerCase().replace('·','')}`}
                className="rounded-xl border border-slate-200 bg-white px-5 py-6 flex flex-col items-center gap-3 hover:shadow-lg hover:-translate-y-1 transition-[box-shadow,transform]">
                <img src={l.src} alt={l.name} className="h-9 w-9 object-contain" loading="lazy" />
                <p className="text-sm font-bold text-slate-700">{l.name}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Meet the AI team ────────────────────────────────────────── */}
      <section className="py-24 px-5 sm:px-8 bg-gradient-to-b from-white to-emerald-50/40">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp} className="max-w-2xl mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700 mb-3">Your 24/7 AI staff</p>
            <h2 style={serif} className="text-3xl sm:text-4xl font-semibold tracking-tighter leading-tight">
              Four AI agents. <em className="text-emerald-700">Zero salaries.</em>
            </h2>
            <p className="text-slate-600 leading-relaxed mt-4">
              Each one is a specialist. Together they close your books while you close deals.
            </p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="ai-team">
            {AI_TEAM.map((a) => (
              <motion.div key={a.slug} {...fadeUp} data-testid={`ai-team-${a.slug}`}
                className="rounded-2xl border border-slate-200 bg-white p-6 hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow]">
                <div className="flex items-center gap-3 mb-4">
                  <span className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${a.bg}`}>
                    <a.Icon className="w-5 h-5 text-white" />
                  </span>
                  <div className="min-w-0">
                    <p className="font-extrabold tracking-tight">{a.name}</p>
                    <p className="text-[11px] uppercase font-bold tracking-wider text-slate-400">{a.role}</p>
                  </div>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-4">{a.blurb}</p>
                <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2.5 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse flex-shrink-0" />
                  <p className="text-[11px] font-semibold text-slate-500 truncate">{a.activity}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Bento feature grid ──────────────────────────────────────── */}
      <section id="features" className="py-24 px-5 sm:px-8 bg-[#F8FAFC] border-y border-slate-200">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp} className="max-w-2xl mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700 mb-3">Every workflow, automated</p>
            <h2 style={serif} className="text-3xl sm:text-4xl font-semibold tracking-tighter leading-tight">
              Accounting that <em className="text-emerald-700">does itself.</em>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* AI Buck auto-filing — col 8 */}
            <motion.div {...fadeUp} className="md:col-span-8 rounded-xl border border-slate-200 bg-white p-8 sm:p-10 hover:shadow-lg transition-shadow" data-testid="feature-ai-buck">
              <div className="flex items-center gap-2.5 mb-4">
                <span className="w-9 h-9 rounded-lg bg-emerald-600 text-white flex items-center justify-center"><ScanLine className="w-4.5 h-4.5" /></span>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">AI Buck · Document auto-filing</p>
              </div>
              <h3 className="text-2xl font-extrabold tracking-tight mb-3">Drop any file. It's read, named &amp; filed.</h3>
              <p className="text-slate-600 leading-relaxed max-w-lg mb-8">
                Upload or email-forward invoices, receipts, bank statements and tax forms.
                AI Buck detects what each one is and files it into the right folder — automatically.
              </p>
              <div className="rounded-xl border-2 border-dashed border-emerald-300 bg-emerald-50/50 p-5">
                <div className="flex flex-wrap gap-3">
                  {[['DJI-mic-receipt.pdf', 'Receipts & Expenses'], ['acme-invoice-0142.pdf', 'Invoices'], ['chase-march.pdf', 'Bank Statements']].map(([f, dest]) => (
                    <div key={f} className="flex items-center gap-2.5 rounded-lg bg-white border border-slate-200 pl-3 pr-2 py-2 text-xs">
                      <FileText className="w-4 h-4 text-slate-400" />
                      <span className="font-semibold text-slate-700">{f}</span>
                      <ArrowRight className="w-3 h-3 text-emerald-600" />
                      <span className="font-bold text-emerald-700 bg-emerald-50 rounded-md px-2 py-1">{dest}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* CFO Buck chat — col 4 */}
            <motion.div {...fadeUp} className="md:col-span-4 rounded-xl border border-slate-200 bg-white p-8 hover:shadow-lg transition-shadow flex flex-col" data-testid="feature-cfo-buck">
              <div className="flex items-center gap-2.5 mb-4">
                <span className="w-9 h-9 rounded-lg bg-[#064E3B] text-white flex items-center justify-center"><MessageSquare className="w-4.5 h-4.5" /></span>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">CFO Buck</p>
              </div>
              <h3 className="text-2xl font-extrabold tracking-tight mb-3">Ask anything.</h3>
              <div className="mt-auto space-y-3 text-[13px]">
                <div className="rounded-2xl rounded-br-md bg-emerald-600 text-white px-4 py-2.5 ml-8">Am I profitable this quarter?</div>
                <div className="rounded-2xl rounded-bl-md bg-slate-100 text-slate-700 px-4 py-2.5 mr-4 leading-relaxed">
                  Yes — net profit is <strong>$18,440</strong>, up 12% vs last quarter. Your biggest driver: faster invoice collection.
                </div>
              </div>
            </motion.div>

            {/* Row 2: three col-4 features */}
            {[
              [Receipt, 'Instantly payable invoices', 'Send branded invoices your customers pay online in one click — card, ACH & Apple Pay via Stripe.'],
              [Landmark, 'Live bank feeds', 'Connect any US bank through Plaid. The Auto-Categorizer sorts transactions while you sleep.'],
              [BarChart3, 'Cash flow & tax savings', 'Real-time dashboards, P&L in seconds, and an AI tax-savings finder that hunts deductions year-round.'],
            ].map(([Icon, t, s]) => (
              <motion.div key={t} {...fadeUp} className="md:col-span-4 rounded-xl border border-slate-200 bg-white p-8 hover:shadow-lg hover:-translate-y-1 transition-[box-shadow,transform]">
                <span className="w-11 h-11 rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-700 flex items-center justify-center mb-5">
                  <Icon className="w-5 h-5" />
                </span>
                <h3 className="text-lg font-extrabold tracking-tight mb-2">{t}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Everything our firm handles (full capability menu) ──────── */}
      <section id="firm-menu" className="py-24 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp} className="max-w-2xl mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700 mb-3">The full capability menu</p>
            <h2 style={serif} className="text-3xl sm:text-4xl font-semibold tracking-tighter leading-tight">
              Everything a real firm does. <em className="text-emerald-700">Under one roof.</em>
            </h2>
            <p className="text-slate-600 leading-relaxed mt-4">
              CountBucks isn&apos;t a lightweight app with a bookkeeping bolt-on. It&apos;s the full stack
              of what a modern accounting firm handles — engineered into one platform, powered by AI.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="firm-menu">
            {FIRM_MENU.map((c) => {
              const tint = TINTS[c.tint];
              return (
                <motion.div key={c.title} {...fadeUp} data-testid={`firm-menu-${c.tint}`}
                  className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-7 hover:shadow-lg hover:-translate-y-1 transition-[box-shadow,transform] flex flex-col">
                  <div className="flex items-center gap-3 mb-5">
                    <span className={`w-11 h-11 rounded-xl ${tint.badge} text-white flex items-center justify-center flex-shrink-0`}>
                      <c.Icon className="w-5 h-5" />
                    </span>
                    <h3 className="text-lg font-extrabold tracking-tight">{c.title}</h3>
                  </div>
                  <ul className="space-y-2 flex-1">
                    {c.items.map((it) => (
                      <li key={it} className="flex items-start gap-2.5 text-[13.5px] text-slate-700 leading-relaxed">
                        <span className={`mt-1.5 w-1.5 h-1.5 rounded-full ${tint.dot} flex-shrink-0`} />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>

          <motion.div {...fadeUp} className="mt-12 rounded-2xl bg-gradient-to-r from-emerald-50 via-white to-emerald-50 border border-emerald-100 p-6 sm:p-7 flex flex-wrap items-center justify-between gap-5">
            <div className="flex items-center gap-4 flex-1 min-w-[240px]">
              <span className="w-11 h-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center flex-shrink-0"><Sparkles className="w-5 h-5" /></span>
              <div>
                <p className="font-extrabold tracking-tight">48+ workflows. One login. Zero add-ons.</p>
                <p className="text-sm text-slate-600 leading-snug mt-0.5">Everything above is included in every plan — no per-feature upsells.</p>
              </div>
            </div>
            <Link to={SIGNUP} data-testid="firm-menu-cta"
              className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-6 py-3 text-sm transition-[background-color,transform] hover:-translate-y-0.5">
              Explore all features <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── A day in your books with AI ─────────────────────────────── */}
      <section className="py-24 px-5 sm:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div {...fadeUp} className="max-w-2xl mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700 mb-3">While you sleep, ship, sell</p>
            <h2 style={serif} className="text-3xl sm:text-4xl font-semibold tracking-tighter leading-tight">
              A day in your books, <em className="text-emerald-700">handled by AI.</em>
            </h2>
            <p className="text-slate-600 leading-relaxed mt-4">
              You focus on customers. Your AI team quietly closes your books in the background.
            </p>
          </motion.div>

          <div className="relative" data-testid="day-timeline">
            <div className="absolute left-6 top-4 bottom-4 w-px bg-gradient-to-b from-emerald-200 via-emerald-500 to-emerald-200 hidden sm:block" />
            <div className="space-y-4">
              {DAY_TIMELINE.map((t) => (
                <motion.div key={t.time} {...fadeUp} className="flex gap-5 sm:gap-6 items-start">
                  <span className={`w-12 h-12 rounded-full flex items-center justify-center ring-4 ring-white flex-shrink-0 relative z-10 ${t.bg}`}>
                    <t.Icon className="w-5 h-5 text-white" />
                  </span>
                  <div className="flex-1 rounded-xl border border-slate-200 bg-white p-5 hover:shadow-md transition-shadow">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-1.5">
                      <span className="text-xs font-extrabold text-emerald-700 tracking-wider">{t.time}</span>
                      <p className="font-extrabold tracking-tight">{t.title}</p>
                      <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500 border border-slate-200 rounded-full px-2 py-0.5">{t.who}</span>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed">{t.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Ask CountBucks anything (interactive) ───────────────────── */}
      <section className="py-24 px-5 sm:px-8 bg-[#F8FAFC] border-y border-slate-200">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-10 items-center">
          <motion.div {...fadeUp} className="lg:col-span-5">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700 mb-3">Try it — no signup</p>
            <h2 style={serif} className="text-3xl sm:text-4xl font-semibold tracking-tighter leading-tight mb-5">
              Ask CountBucks <em className="text-emerald-700">anything.</em>
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              CFO Buck reads your ledger, invoices and tax history — then answers in plain English.
              Tap a question to see it in action.
            </p>
            <div className="space-y-2" data-testid="chat-prompt-list">
              {CHAT_PROMPTS.map((p, i) => (
                <button key={p.q} onClick={() => setActivePrompt(i)} data-testid={`chat-prompt-${i}`}
                  className={`w-full text-left rounded-full border px-4 py-2.5 text-sm font-semibold transition-all ${activePrompt === i ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-300 hover:text-emerald-700'}`}>
                  {p.q}
                </button>
              ))}
            </div>
          </motion.div>

          <motion.div {...fadeUp} className="lg:col-span-7 rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_16px_40px_-16px_rgba(2,44,34,0.15)]" data-testid="chat-teaser">
            <div className="flex items-center gap-2.5 pb-4 border-b border-slate-100">
              <span className="w-8 h-8 rounded-lg bg-[#064E3B] text-white flex items-center justify-center"><Bot className="w-4 h-4" /></span>
              <p className="font-extrabold text-sm">CFO Buck</p>
              <span className="ml-auto text-[10px] uppercase font-bold tracking-wider text-emerald-700 bg-emerald-50 rounded-full px-2 py-1">Claude · GPT · Gemini</span>
            </div>
            <div className="pt-5 space-y-4 min-h-[180px]">
              <motion.div key={`q-${activePrompt}`} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
                className="rounded-2xl rounded-br-md bg-emerald-600 text-white px-4 py-2.5 text-sm ml-16" data-testid="chat-question">
                {CHAT_PROMPTS[activePrompt].q}
              </motion.div>
              <motion.div key={`a-${activePrompt}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.15 }}
                className="rounded-2xl rounded-bl-md bg-slate-50 border border-slate-100 text-slate-700 px-4 py-3.5 text-sm mr-8 leading-relaxed whitespace-pre-line" data-testid="chat-answer">
                {CHAT_PROMPTS[activePrompt].a}
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Business types ──────────────────────────────────────────── */}
      <section className="py-24 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp} className="max-w-2xl mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700 mb-3">For every kind of business</p>
            <h2 style={serif} className="text-3xl sm:text-4xl font-semibold tracking-tighter leading-tight">
              Built for how <em className="text-emerald-700">you</em> work.
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              ['https://images.pexels.com/photos/30020992/pexels-photo-30020992.jpeg?auto=compress&cs=tinysrgb&w=800', 'Retail & e-commerce',
                ['Track sales & inventory costs', 'Sync Stripe payouts automatically', 'Know your margin per product line']],
              ['https://images.pexels.com/photos/34670926/pexels-photo-34670926.jpeg?auto=compress&cs=tinysrgb&w=800', 'Construction & trades',
                ['Invoice from your phone on-site', 'Snap receipts — AI files them', 'Job costing & time tracking built in']],
              ['https://images.pexels.com/photos/5920775/pexels-photo-5920775.jpeg?auto=compress&cs=tinysrgb&w=800', 'Services & consultants',
                ['Billable hours to invoice in a click', 'Recurring invoices on autopilot', 'Client portal your customers love']],
            ].map(([img, title, list]) => (
              <motion.div key={title} {...fadeUp} className="rounded-xl border border-slate-200 bg-white overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-[box-shadow,transform]">
                <img src={img} alt={title} className="h-44 w-full object-cover" loading="lazy" />
                <div className="p-7">
                  <h3 className="text-lg font-extrabold tracking-tight mb-4">{title}</h3>
                  <ul className="space-y-2.5">{list.map((li) => <CheckItem key={li}>{li}</CheckItem>)}</ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Dark green migration band ───────────────────────────────── */}
      <section id="switch" className="relative py-24 px-5 sm:px-8 bg-[#064E3B] text-[#F8FAFC] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.35]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.06'/%3E%3C/svg%3E\")" }} />
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center relative">
          <motion.div {...fadeUp}>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-300 mb-3">Leaving QuickBooks?</p>
            <h2 style={serif} className="text-3xl sm:text-5xl font-semibold tracking-tighter leading-tight mb-5">
              Switch in about <em className="text-emerald-300">3 minutes.</em>
            </h2>
            <p className="text-emerald-100/80 leading-relaxed max-w-lg mb-8">
              Export from QuickBooks, drop the file in, and CountBucks rebuilds your chart of
              accounts, customers, vendors and open invoices. White-glove plans include full
              historical migration — done for you.
            </p>
            <Link to={SIGNUP} data-testid="switch-cta"
              className="inline-flex items-center gap-2 rounded-full bg-white text-[#064E3B] font-bold px-7 py-3.5 hover:bg-emerald-50 transition-[background-color,transform] hover:-translate-y-0.5">
              Start my switch <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
          <motion.div {...fadeUp} className="rounded-2xl bg-white/[0.06] border border-white/15 backdrop-blur p-7">
            <p className="text-sm font-bold mb-5 flex items-center gap-2"><ArrowRightLeft className="w-4 h-4 text-emerald-300" /> Migration progress</p>
            {[['Chart of accounts', 100], ['Customers & vendors', 100], ['Open invoices', 100], ['Historical transactions', 64]].map(([label, pct]) => (
              <div key={label} className="mb-4">
                <div className="flex justify-between text-xs font-semibold text-emerald-100/80 mb-1.5">
                  <span>{label}</span><span>{pct}%</span>
                </div>
                <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full rounded-full bg-emerald-400" style={{ width: `${pct}%` }} />
                </div>
              </div>
            ))}
            <p className="text-xs text-emerald-100/60 mt-5 flex items-center gap-1.5"><Zap className="w-3.5 h-3.5" /> Average Fresh Start migration: 2m 48s</p>
          </motion.div>
        </div>
      </section>

      {/* ── AI safety & trust strip ─────────────────────────────────── */}
      <section className="py-24 px-5 sm:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp} className="max-w-2xl mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700 mb-3">AI you can actually trust</p>
            <h2 style={serif} className="text-3xl sm:text-4xl font-semibold tracking-tighter leading-tight">
              No hallucinations. <em className="text-emerald-700">Ever.</em>
            </h2>
            <p className="text-slate-600 leading-relaxed mt-4 max-w-xl">
              Financial AI without guardrails is a liability. CountBucks was built with hallucination
              safeguards from day one — every answer traceable, every action reversible.
            </p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="ai-safety">
            {SAFETY.map((s) => (
              <motion.div key={s.t} {...fadeUp} data-testid={`ai-safety-${s.t.split(' ')[0].toLowerCase()}`}
                className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-6 hover:shadow-md transition-shadow">
                <span className="w-11 h-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center mb-4">
                  <s.Icon className="w-5 h-5" />
                </span>
                <p className="font-extrabold tracking-tight mb-1.5">{s.t}</p>
                <p className="text-sm text-slate-600 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ─────────────────────────────────────────────────── */}
      <section id="pricing" className="py-24 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp} className="max-w-2xl mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700 mb-3">Simple pricing</p>
            <h2 style={serif} className="text-3xl sm:text-4xl font-semibold tracking-tighter leading-tight">
              Start small. <em className="text-emerald-700">Scale forever.</em>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {TIERS.map((t) => (
              <motion.div key={t.id} {...fadeUp}
                className={`rounded-xl border bg-white p-8 flex flex-col relative hover:shadow-lg transition-shadow ${t.popular ? 'border-emerald-600 shadow-[0_16px_40px_-16px_rgba(16,185,129,0.35)]' : 'border-slate-200'}`}
                data-testid={`pricing-tier-${t.id}`}>
                {t.popular && (
                  <span className="absolute -top-3 left-8 text-[11px] font-extrabold uppercase tracking-wider bg-emerald-600 text-white rounded-full px-3 py-1">Most popular</span>
                )}
                <p className="font-extrabold text-lg">{t.name}</p>
                <p className="text-sm text-slate-500 mb-5">{t.tagline}</p>
                <p className="mb-6"><span className="text-4xl font-extrabold tracking-tight">${t.price}</span><span className="text-sm text-slate-500 font-semibold"> /month</span></p>
                <ul className="space-y-2.5 mb-8">{t.features.map((f) => <CheckItem key={f}>{f}</CheckItem>)}</ul>
                <Link to={`/login?mode=register&plan=${t.id}&cadence=monthly`} data-testid={`pricing-cta-${t.id}`}
                  className={`mt-auto text-center rounded-full font-bold px-6 py-3 transition-[background-color,transform] hover:-translate-y-0.5 ${t.popular ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'border border-slate-300 hover:border-emerald-600 hover:text-emerald-700'}`}>
                  Get started
                </Link>
              </motion.div>
            ))}
          </div>

          {/* White-glove tier band */}
          <motion.div {...fadeUp} className="grid md:grid-cols-2 gap-6">
            {WHITE_GLOVE.map((t) => (
              <div key={t.id} className="rounded-xl border border-slate-200 bg-[#F8FAFC] p-8 flex flex-col sm:flex-row gap-7 hover:shadow-lg transition-shadow" data-testid={`pricing-tier-${t.id}`}>
                <div className="sm:w-44 flex-shrink-0">
                  <p className="text-[11px] font-extrabold uppercase tracking-wider text-emerald-700 mb-1.5">White glove</p>
                  <p className="font-extrabold text-lg leading-tight">{t.name}</p>
                  <p className="text-sm text-slate-500 mb-3">{t.tagline}</p>
                  <p><span className="text-3xl font-extrabold tracking-tight">${t.price}</span><span className="text-sm text-slate-500 font-semibold"> /mo</span></p>
                </div>
                <div className="flex-1">
                  <ul className="space-y-2.5 mb-5">{t.features.map((f) => <CheckItem key={f}>{f}</CheckItem>)}</ul>
                  <Link to={`/login?mode=register&plan=${t.id}&cadence=monthly`} data-testid={`pricing-cta-${t.id}`}
                    className="inline-flex items-center gap-1.5 text-sm font-bold text-emerald-700 hover:text-emerald-800">
                    Get started <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────── */}
      <section className="py-24 px-5 sm:px-8 bg-[#F8FAFC] border-y border-slate-200">
        <div className="max-w-3xl mx-auto">
          <motion.h2 {...fadeUp} style={serif} className="text-3xl sm:text-4xl font-semibold tracking-tighter leading-tight mb-12">
            Frequently asked <em className="text-emerald-700">questions.</em>
          </motion.h2>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <motion.div key={f.q} {...fadeUp} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} data-testid={`faq-${i}`}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left font-bold hover:text-emerald-700 transition-colors">
                  {f.q}
                  <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <p className="px-6 pb-5 text-sm text-slate-600 leading-relaxed">{f.a}</p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA + footer ──────────────────────────────────────── */}
      <section className="py-28 px-5 sm:px-8 text-center">
        <motion.div {...fadeUp} className="max-w-2xl mx-auto">
          <div className="flex justify-center gap-1 mb-6">
            {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />)}
          </div>
          <h2 style={serif} className="text-3xl sm:text-5xl font-semibold tracking-tighter leading-tight mb-5">
            Your books, <em className="text-emerald-700">handled.</em>
          </h2>
          <p className="text-slate-600 leading-relaxed mb-8">
            Join business owners who stopped doing data entry and started making decisions.
          </p>
          <Link to={SIGNUP} data-testid="footer-cta-btn"
            className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-8 py-4 text-base transition-[background-color,transform] hover:-translate-y-0.5">
            Start 7 days free <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </section>

      <footer className="bg-[#022C22] text-emerald-100/70 px-5 sm:px-8 pt-16 pb-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 pb-12 border-b border-white/10">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="w-8 h-8 rounded-lg bg-emerald-500 text-white flex items-center justify-center font-extrabold text-sm">CB</span>
                <span className="font-extrabold text-lg text-white">CountBucks</span>
              </div>
              <p className="text-sm leading-relaxed">AI-powered accounting for small businesses that would rather run their business.</p>
            </div>
            <div>
              <p className="text-white font-bold text-sm mb-4">Product</p>
              <div className="space-y-2.5 text-sm">
                <a href="#features" className="block hover:text-white transition-colors">Features</a>
                <a href="#ai" className="block hover:text-white transition-colors">AI Models</a>
                <a href="#pricing" className="block hover:text-white transition-colors">Pricing</a>
                <a href="#switch" className="block hover:text-white transition-colors">Switch from QuickBooks</a>
              </div>
            </div>
            <div>
              <p className="text-white font-bold text-sm mb-4">Features</p>
              <div className="space-y-2.5 text-sm">
                <p>Invoicing &amp; payments</p>
                <p>Bank feeds &amp; reconciliation</p>
                <p>Document hub &amp; e-signatures</p>
                <p>Reports &amp; tax savings</p>
              </div>
            </div>
            <div>
              <p className="text-white font-bold text-sm mb-4">Company</p>
              <div className="space-y-2.5 text-sm">
                <Link to="/login" className="block hover:text-white transition-colors">Sign in</Link>
                <Link to="/privacy" className="block hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy</Link>
                <Link to="/terms" className="block hover:text-white transition-colors" data-testid="footer-terms-link">Terms</Link>
              </div>
            </div>
          </div>
          <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs">
            <p>© {new Date().getFullYear()} CountBucks. All rights reserved.</p>
            <p className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Bank-level encryption · Plaid · Stripe</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
